import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperIconComponent, PaperKbdComponent, PaperTagComponent } from '@ricoh-paper/angular';
import type { CmdkSpaceItem } from '../../models/cmdk.model';

/**
 * A space row — room, desk, parking, locker.
 *
 * The availability tag is the only thing here that is not shared with every
 * other row kind, and it is the reason the row exists at all: "Boardroom" is
 * not a useful answer, "Boardroom, free" is.
 *
 * `unknown` renders NO tag. That state is reached from any surface without a
 * focused building, where the estate-wide search returns no availability at
 * all — and a grey "Unknown" chip on every row would be noise that teaches the
 * user to ignore the column where the real answer appears.
 */
@Component({
  selector: 'im-cmdk-row-space',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, PaperKbdComponent, PaperTagComponent],
  templateUrl: './cmdk-row-space.component.html',
  styleUrl: './cmdk-row-space.component.scss',
})
export class CmdkRowSpaceComponent {
  private readonly transloco = inject(TranslocoService);

  readonly item = input.required<CmdkSpaceItem>();
  readonly selected = input(false);
  readonly quickOpenIndex = input<number | null>(null);

  protected readonly quickOpenKey = computed(() => {
    const index = this.quickOpenIndex();
    return index === null ? null : String(index + 1);
  });

  /** emerald / rose / amber — the only intents paper-tag accepts. */
  protected readonly tagIntent = computed(() => {
    switch (this.item().availability) {
      case 'available': return 'emerald' as const;
      case 'busy': return 'rose' as const;
      default: return 'amber' as const;
    }
  });

  protected readonly tagLabel = computed(() => {
    const availability = this.item().availability;
    if (availability === 'unknown') return null;
    return this.transloco.translate(`shell.cmdk.availability.${availability}`);
  });

  /** A room, a desk and a car park should not share one glyph. */
  protected readonly icon = computed(() => {
    const type = this.item().spaceType.toLowerCase();
    if (type.includes('desk')) return 'monitor';
    if (type.includes('park')) return 'car';
    if (type.includes('locker')) return 'lock';
    return 'users';
  });
}
